import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Heading,
  PrimaryButton,
  Text,
  TertiaryButton,
} from '@moonpig/launchpad-components'
import { FormProvider, useForm } from 'react-hook-form'
import {
  useAuthFetch,
  useGoogleOneTap,
  useLoggedIn,
} from '@moonpig/web-core-auth'
import { Region } from '@moonpig/web-core-types'
import { logger } from '@moonpig/web-core-monitoring'
import { useFlag } from '@moonpig/web-purchase-flags'
import { Form } from '@moonpig/web-shared-components'
import {
  CookieCategory,
  getBrowserCookies,
  setBrowserCookie,
} from '@moonpig/web-core-browser-storage'
import {
  useExperimentString,
  useExperimentBoolean,
} from '@moonpig/web-core-eppo/browser'
import { createSha256Hash } from '@moonpig/web-core-utils'
import { IconSystemChevronLeft } from '@moonpig/launchpad-assets'
import { useStoreId } from '@moonpig/web-core-stores'
import { useAuthenticationLocaleText } from '../../text-localisation'
import { authenticationService } from './AuthenticationService'
import { redirect } from './utils'
import {
  AccountLookupResponse,
  AuthenticationFormInput,
  FormStep,
  LoginInput,
  RegisterInput,
  ValidateEmailResponse,
  ValidationError,
} from './types'

import { ForgotPasswordModal } from './ForgotPasswordModal'
import { EmailField } from './EmailField'
import { PasswordField } from './PasswordField'
import { RegistrationFields } from './RegistrationFields'
import {
  trackAuthenticationAccountRecoveryOnPasscodeFormSubmit,
  trackAuthenticationAccountRecoveryResendTheCodeCTA,
  trackAuthenticationBackCTA,
  trackAuthenticationContinueCTA,
  trackAuthenticationImpression,
  trackAuthenticationLoginCTA,
  trackAuthenticationLoginFailure,
  trackAuthenticationLoginSuccess,
  trackAuthenticationMagicLinkFailure,
  trackAuthenticationMagicLinkSuccess,
  trackAuthenticationOnPasscodeFormSubmit,
  trackAuthenticationPasscodeError,
  trackAuthenticationPasscodeRegistrationError,
  trackAuthenticationPasscodeRegistrationSuccess,
  trackAuthenticationPreRegisterCTA,
  trackAuthenticationRegisterCTA,
  trackAuthenticationRegisterFailure,
  trackAuthenticationRegisterSuccess,
  trackAuthenticationRegistrationOnPasscodeFormSubmit,
  trackAuthenticationPasscodeRegistrationResendTheCodeCTA,
  trackAuthenticationResendTheCodeCTA,
  trackAuthenticationSignInWithCodeCTA,
  trackAuthenticationSignInWithPasswordCTA,
  trackAuthenticationValidationErrors,
  trackForgotPasswordCTA,
  trackValidateEmailInvalidImpression,
  trackValidateEmailModalDismiss,
} from './tracking'
import { ValidateEmailWarningModal } from './ValidateEmailWarningModal'
import { SocialLoginButtonsWrapper } from './SocialLoginButtonsWrapper'
import { TermsAndConditions } from './TermsAndConditions'
import { LoadingOverlay } from '../shared/LoadingOverlay'
import { SignInDivider } from './SignInDivider'
import { SignInWithACodeButton } from './SignInWithCode'
import { SignInWithCodeForm } from './SignInWithCode/SignInWithCodeForm/SignInWithCodeForm'
import { SignInWithPasswordButton } from './SingInWithPassword'
import { Envelope } from '../Envelope'

export const AuthenticationWizard: React.FC<{
  region: Region
  returnUrl?: string
  magicLinkToken?: string
  magicLinkAuthRequired?: boolean
  source?: string
  flags?: Record<string, boolean>
}> = ({
  region,
  returnUrl,
  magicLinkToken,
  magicLinkAuthRequired = true,
  source,
  flags = {},
}) => {
  const storeId = useStoreId()

  const [magicLoginStatus, setMagicLoginStatus] = useState(
    magicLinkToken ? 'pre-login' : 'no-token',
  )
  const featureValidateEmailEnabled = useFlag('authwizard-validate-email')

  const featureAccountRecoveryWithCode =
    useExperimentString('account-recovery-with-code', {
      fallback: 'control',
    })?.toLowerCase() === 'enabled'

  const featureDefaultToLoginWithCode = useExperimentBoolean(
    'customer-default-to-login-with-code',
    { fallback: false },
  )

  const featurePasswordlessRegistration = useExperimentBoolean(
    'customer-passwordless-registration',
    {
      fallback: false,
    },
  )

  const featureCustomCodeInput = flags['customer-auth-custom-code-input']

  const tempFormContainerProps = featureCustomCodeInput
    ? {}
    : { maxWidth: { xs: 1, xl: 4 / 5 } }

  const localiseText = useAuthenticationLocaleText()

  const [pageError, setPageError] = useState('')
  const [isLockedAccount, setIsLockedAccount] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  )

  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)
  const [showValidateEmailModal, setShowValidateEmailModal] = useState(false)
  const [isEmailUnreachable, setIsEmailUnreachable] = useState(false)

  const [showUndeliverableEmailAlert, setShowUndeliverableEmailAlert] =
    useState(false)
  const [undeliverableEmail, setUndeliverableEmail] = useState<
    undefined | string
  >(undefined)

  const authFetch = useAuthFetch()
  const { accountLookup, login, register, validateEmail, magicLinkLogin } =
    authenticationService(authFetch, region)
  const methods = useForm<AuthenticationFormInput>({
    defaultValues: {
      email: undefined,
      password: undefined,
      firstName: undefined,
      lastName: undefined,
      termsAccepted: true,
      formStep: FormStep.WELCOME,
      flowId: undefined,
    },
    delayError: 100,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
  })

  const { oneTapState } = useGoogleOneTap()

  const { loggedIn } = useLoggedIn()

  const {
    getValues,
    clearErrors: resetFormErrors,
    handleSubmit,
    setValue,
    setFocus,
    watch,
  } = methods

  const formStep: FormStep = watch('formStep')
  const formEmail: string = watch('email')

  useEffect(() => {
    if (magicLoginStatus === 'no-token' || magicLoginStatus === 'failed') {
      trackAuthenticationImpression(formStep)
    }
  }, [formStep, magicLoginStatus])

  useEffect(() => {
    if (oneTapState === 'success') {
      return redirect(region, returnUrl)
    }

    setIsLoading(oneTapState === 'authenticating')
  }, [oneTapState, region, returnUrl])

  useEffect(() => {
    const magicLinkFailure = (errorMessage?: string) => {
      trackAuthenticationMagicLinkFailure(errorMessage, loggedIn)
      if (!loggedIn && magicLinkAuthRequired) {
        setIsLoading(false)
        setMagicLoginStatus('failed')
        return
      }
      redirect(region, returnUrl)
    }

    const loginWithMagicLink = async () => {
      if (magicLoginStatus !== 'pre-login' || !magicLinkToken) return
      setMagicLoginStatus('logging-in')
      setIsLoading(true)
      try {
        const result = await magicLinkLogin(magicLinkToken)
        if (!result.success) {
          magicLinkFailure(result.errorMessage)
          return
        }
        trackAuthenticationMagicLinkSuccess(result.skipped)
        redirect(region, returnUrl)
      } catch (error) {
        magicLinkFailure(undefined)
      }
    }
    loginWithMagicLink()
  }, [
    magicLinkToken,
    magicLinkAuthRequired,
    region,
    returnUrl,
    magicLinkLogin,
    loggedIn,
    magicLoginStatus,
  ])

  const changeFormStep = (step: FormStep) => {
    clearErrors()
    setValue('formStep', step)
    if (step !== FormStep.WELCOME) {
      setFocus('password')
    }
  }

  const primaryCTAText = () => {
    switch (formStep) {
      case FormStep.LOGIN:
        return localiseText('auth_wizard.sign_in')
      case FormStep.REGISTER:
        return localiseText('auth_wizard.register')
      default:
        return localiseText('auth_wizard.continue')
    }
  }

  const clearErrors = () => {
    resetFormErrors()
    setPageError('')
    setIsLockedAccount(false)
    setValidationErrors([])
  }

  const onSubmit = async ({
    email,
    password,
    firstName,
    lastName,
    termsAccepted,
  }: AuthenticationFormInput) => {
    setPageError('')
    switch (formStep) {
      case FormStep.WELCOME:
        trackAuthenticationContinueCTA()
        await onWelcomeSubmit(email)
        break
      case FormStep.LOGIN:
        trackAuthenticationLoginCTA()
        await onLoginSubmit({ email, password })
        break
      case FormStep.REGISTER:
        trackAuthenticationRegisterCTA()
        await onRegisterSubmit({
          email,
          password,
          firstName,
          lastName,
          termsAccepted,
        })
        break
      case FormStep.CODE_PRE_REGISTER:
        trackAuthenticationPreRegisterCTA()
        await onPreRegisterSubmit()
        break
      /* istanbul ignore next */
      case FormStep.CODE_LOGIN:
      case FormStep.CODE_RECOVERY:
      case FormStep.CODE_REGISTER:
        // Hopefully temp. Stops code login autosubmit submitting this form and hitting error default
        break
      /* istanbul ignore next */
      default:
        logger.fixToday(`Unhandled form step submission ${formStep}`)
        break
    }
  }

  const onWelcomeSubmit = async (email: string) => {
    /* istanbul ignore next */
    if (!navigator.cookieEnabled) {
      // eslint-disable-next-line no-alert
      alert(localiseText('auth_wizard.sign_in.cookies_alert'))
      return
    }
    setIsLoading(true)
    const { response, errorMessage } = await accountLookup({ email })
    setIsLoading(false)
    switch (response) {
      case featureDefaultToLoginWithCode &&
        AccountLookupResponse.ACCOUNT_EXISTS_WITH_PASSWORD:
        changeFormStep(FormStep.CODE_LOGIN)
        break
      case AccountLookupResponse.ACCOUNT_EXISTS_WITH_PASSWORD:
        changeFormStep(FormStep.LOGIN)
        break
      case AccountLookupResponse.ACCOUNT_EXISTS_WITHOUT_PASSWORD:
        changeFormStep(FormStep.CODE_LOGIN)
        break
      case AccountLookupResponse.ACCOUNT_NOT_EXIST:
        if (featureValidateEmailEnabled) {
          emailValidate(email)
        } else {
          const regStep = featurePasswordlessRegistration
            ? FormStep.CODE_PRE_REGISTER
            : FormStep.REGISTER

          changeFormStep(regStep)
        }
        break
      case AccountLookupResponse.ACCOUNT_DEACTIVATED:
        setPageError(localiseText('auth_wizard.welcome.error.account_disabled'))
        break
      default:
        setPageError(errorMessage ?? localiseText('auth_wizard.welcome.error'))
        break
    }
  }

  const onPreRegisterSubmit = async () => {
    changeFormStep(FormStep.CODE_REGISTER)
  }

  const onRegisterSubmit = async ({
    email,
    password,
    firstName,
    lastName,
    termsAccepted,
  }: RegisterInput) => {
    setIsLoading(true)
    const response = await register({
      email,
      password,
      firstName,
      lastName,
      termsAccepted,
      source,
    })

    const { success, errorMessage, errors } = response
    if (success) {
      trackAuthenticationRegisterSuccess(isEmailUnreachable)
      return redirect(region, returnUrl)
    }

    const localisedMessage =
      errorMessage ?? localiseText('auth_wizard.register.error')
    trackAuthenticationRegisterFailure(errorMessage)
    setPageError(localisedMessage)
    setValidationErrors(errors ?? [])

    setIsLoading(false)
  }

  const onLoginSubmit = async ({ email, password }: LoginInput) => {
    setIsLockedAccount(false)
    setIsLoading(true)

    const { success, accountLocked, errorMessage } = await login({
      email,
      password,
    })
    if (success) {
      trackAuthenticationLoginSuccess('password')
      return redirect(region, returnUrl)
    }
    if (accountLocked) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setPageError(errorMessage!)
      setIsLockedAccount(true)
      trackAuthenticationLoginFailure(errorMessage)
    } else {
      const localisedMessage =
        errorMessage ?? localiseText('auth_wizard.sign_in.error')
      trackAuthenticationLoginFailure(localisedMessage)
      setPageError(localisedMessage)
    }
    setIsLoading(false)
  }

  const standardEmailValidation = async (email: string): Promise<void> => {
    const hashedEmail = await createSha256Hash(email)
    const { mnpg_authwizard_validate_email: cookie } = getBrowserCookies()
    if (cookie && cookie === hashedEmail) {
      changeFormStep(FormStep.REGISTER)
      return
    }
    setIsLoading(true)
    const { response } = await validateEmail({ email })
    setIsLoading(false)

    if (response === ValidateEmailResponse.EMAIL_INVALID) {
      setIsEmailUnreachable(true)
      setShowValidateEmailModal(true)
      trackValidateEmailInvalidImpression()

      setBrowserCookie(
        'mnpg_authwizard_validate_email',
        hashedEmail,
        {
          maxAge: 30 * 60,
          path: '/',
        },
        CookieCategory.STRICTLY_NECESSARY,
      )
    } else {
      setIsEmailUnreachable(false)
      changeFormStep(FormStep.REGISTER)
    }
  }

  const passwordlessEmailValidation = async (email: string): Promise<void> => {
    if (undeliverableEmail === email) {
      changeFormStep(FormStep.CODE_PRE_REGISTER)
      return
    }

    setIsLoading(true)
    const { response } = await validateEmail({ email })
    setIsLoading(false)

    if (response === ValidateEmailResponse.EMAIL_INVALID) {
      setUndeliverableEmail(email)
    } else {
      setIsEmailUnreachable(false)
      changeFormStep(FormStep.CODE_PRE_REGISTER)
    }
  }

  const emailValidate = featurePasswordlessRegistration
    ? passwordlessEmailValidation
    : standardEmailValidation

  useEffect(() => {
    const emailSet = formEmail !== undefined
    const emailUndeliverable = undeliverableEmail === formEmail
    const onWelcomeStep = formStep === FormStep.WELCOME
    const showAlert = emailSet && emailUndeliverable && onWelcomeStep
    setShowUndeliverableEmailAlert(showAlert)
  }, [undeliverableEmail, formEmail, formStep])

  const onForgotPasswordClick = async () => {
    clearErrors()
    trackForgotPasswordCTA()
    if (featureAccountRecoveryWithCode) {
      changeFormStep(FormStep.CODE_RECOVERY)
      return
    }
    setShowForgotPasswordModal(true)
  }

  // mess

  const showBackButton =
    formStep !== FormStep.WELCOME && !featureCustomCodeInput

  const showNewBackButton =
    formStep !== FormStep.WELCOME && featureCustomCodeInput

  const showSubmitButton =
    formStep !== FormStep.CODE_LOGIN &&
    formStep !== FormStep.CODE_RECOVERY &&
    formStep !== FormStep.CODE_REGISTER

  const showLoginWithCodeButton =
    formStep === FormStep.LOGIN && !featureDefaultToLoginWithCode

  const showLoginWithPasswordButton =
    formStep === FormStep.CODE_LOGIN && featureDefaultToLoginWithCode

  const showCodeLoginIllustration =
    featureCustomCodeInput &&
    (formStep === FormStep.CODE_LOGIN ||
      formStep === FormStep.CODE_RECOVERY ||
      formStep === FormStep.CODE_REGISTER)

  const onCodeLoginClick = () => {
    trackAuthenticationSignInWithCodeCTA()
    changeFormStep(FormStep.CODE_LOGIN)
  }

  const onPasswordLoginClick = () => {
    trackAuthenticationSignInWithPasswordCTA()
    changeFormStep(FormStep.LOGIN)
  }

  const onBackClickToLogin = async () => {
    window.scrollTo(0, 0)
    trackAuthenticationBackCTA(formStep)

    setIsLoading(false)

    if (formStep === FormStep.CODE_LOGIN && !featureDefaultToLoginWithCode) {
      changeFormStep(FormStep.LOGIN)
    } else if (formStep === FormStep.CODE_RECOVERY) {
      changeFormStep(FormStep.LOGIN)
    } else if (formStep === FormStep.CODE_REGISTER) {
      changeFormStep(FormStep.CODE_PRE_REGISTER)
    } else {
      changeFormStep(FormStep.WELCOME)
    }

    setValue('password', '')
  }

  const onEmailChange = () => {
    clearErrors()
    setValue('password', '')
  }

  const dismissEmailValidationModal = () => {
    trackValidateEmailModalDismiss()
    setShowValidateEmailModal(false)
    setValue('email', '')
  }

  const formStepTitle = (): string => {
    switch (formStep) {
      case FormStep.LOGIN:
        return localiseText('auth_wizard.sign_in_title')
      case FormStep.REGISTER:
        return localiseText('auth_wizard.register_title')
      case FormStep.CODE_PRE_REGISTER:
        return localiseText('auth_wizard.pre_register.title')
      case FormStep.CODE_LOGIN:
      case FormStep.CODE_REGISTER:
      case FormStep.CODE_RECOVERY:
        if (featureCustomCodeInput) {
          return localiseText('auth_wizard.sign_in_with_code_form.title_v2')
        }
        return localiseText('auth_wizard.sign_in_with_code_form.title')
      default:
        return localiseText('auth_wizard.welcome_title')
    }
  }

  const getSubtitle = () => {
    if (
      [
        FormStep.CODE_LOGIN,
        FormStep.CODE_RECOVERY,
        FormStep.CODE_REGISTER,
      ].includes(formStep)
    ) {
      return (
        <Text
          data-testid="formStepSubTitle"
          display={'block'}
          textAlign={featureCustomCodeInput ? 'center' : 'left'}
        >
          {localiseText('auth_wizard.sign_in_with_code_form.description')}{' '}
          <strong>{getValues().email}</strong>
        </Text>
      )
    }

    if (FormStep.CODE_PRE_REGISTER === formStep) {
      return (
        <Text data-testid="formStepSubTitle" display={'block'}>
          {localiseText('auth_wizard.pre_register.sub_title')}{' '}
          <strong>{getValues().email}</strong>
        </Text>
      )
    }

    if (featurePasswordlessRegistration) {
      return (
        <Text data-testid="formStepSubTitle">
          {localiseText('auth_wizard.welcome_subtitle_passwordless')}
        </Text>
      )
    }

    return (
      <Text
        data-testid="formStepSubTitle"
        display={{ xs: 'block', md: 'none' }}
      >
        {localiseText('auth_wizard.welcome_subtitle')}
      </Text>
    )
  }

  const showSocialLoginOptions = formStep === FormStep.WELCOME

  const onSignInWithCodeSuccess = (loginMethod = 'passcode') => {
    trackAuthenticationLoginSuccess(loginMethod)
    redirect(region, returnUrl)
  }

  const onRegisterWithCodeSuccess = () => {
    trackAuthenticationPasscodeRegistrationSuccess()
    redirect(region, returnUrl)
  }

  const showTermsAndConditions =
    formStep === FormStep.WELCOME || !featurePasswordlessRegistration

  return (
    <>
      {showNewBackButton && (
        <TertiaryButton
          type="reset"
          onClick={onBackClickToLogin}
          leadingIcon={<IconSystemChevronLeft />}
          style={{
            minHeight: '24px',
            height: '24px',
            textDecoration: 'none',
            textAlign: 'left',
            left: '-8px',
            padding: 0,
          }}
          data-testid="back-v2"
        >
          {localiseText('auth_wizard.back')}
        </TertiaryButton>
      )}
      {showCodeLoginIllustration && (
        <Box mt={8} mb={4} justifyContent="center" display="flex">
          <Envelope
            data-testid="authWizardCodeEnvelope"
            brand={storeId === 'nl' ? 'greetz' : 'moonpig'}
            width={'100px'}
            email={getValues().email}
            animationPaused={isLoading}
          />
        </Box>
      )}
      <Heading
        level="h2"
        as="h1"
        mb={4}
        mt={6}
        typography={'typeDesktopDisplay02'}
        textAlign={showCodeLoginIllustration ? 'center' : 'left'}
        data-testid="formStepTitle"
        dangerouslySetInnerHTML={{
          __html: formStepTitle(),
        }}
      />
      {getSubtitle()}
      <Box mb={{ xs: 8, md: 8 }} mt={8} {...tempFormContainerProps}>
        <FormProvider {...methods}>
          <Form
            method="post"
            onSubmit={handleSubmit(
              data => onSubmit(data),
              errors => {
                trackAuthenticationValidationErrors(formStep, errors)
              },
            )}
            noValidate
          >
            <EmailField
              validationErrors={validationErrors}
              onChange={onEmailChange}
            />
            <PasswordField
              validationErrors={validationErrors}
              handleForgottenPassword={onForgotPasswordClick}
            />
            {/* First and Last name fields*/}
            <RegistrationFields
              validationErrors={validationErrors}
              region={region}
            />
            {formStep === FormStep.CODE_LOGIN && (
              <SignInWithCodeForm
                action="login"
                email={getValues().email}
                returnUrl={returnUrl}
                onSubmitCallback={trackAuthenticationOnPasscodeFormSubmit}
                onResendCodeCallback={trackAuthenticationResendTheCodeCTA}
                onLoginSuccessCallback={onSignInWithCodeSuccess}
                onSendCodeErrorCallback={errorMessage =>
                  trackAuthenticationPasscodeError('passcode', errorMessage)
                }
                onLoginErrorCallback={errorMessage =>
                  trackAuthenticationPasscodeError('passcode', errorMessage)
                }
                loading={isLoading}
                setLoading={setIsLoading}
                flags={flags}
              />
            )}
            {formStep === FormStep.CODE_REGISTER && (
              <SignInWithCodeForm
                action="register"
                email={getValues().email}
                firstName={getValues().firstName}
                lastName={getValues().lastName}
                returnUrl={returnUrl}
                flags={flags}
                onSubmitCallback={
                  trackAuthenticationRegistrationOnPasscodeFormSubmit
                }
                onResendCodeCallback={
                  trackAuthenticationPasscodeRegistrationResendTheCodeCTA
                }
                onLoginSuccessCallback={onRegisterWithCodeSuccess}
                onSendCodeErrorCallback={errorMessage =>
                  trackAuthenticationPasscodeRegistrationError(errorMessage)
                }
                onLoginErrorCallback={errorMessage =>
                  trackAuthenticationPasscodeRegistrationError(errorMessage)
                }
                loading={isLoading}
                setLoading={setIsLoading}
                submitButtonText={localiseText(
                  'auth_wizard.code_register.submit_button_text',
                )}
              />
            )}
            {formStep === FormStep.CODE_RECOVERY && (
              <SignInWithCodeForm
                action="recovery"
                email={getValues().email}
                returnUrl={returnUrl}
                onSubmitCallback={
                  trackAuthenticationAccountRecoveryOnPasscodeFormSubmit
                }
                onResendCodeCallback={
                  trackAuthenticationAccountRecoveryResendTheCodeCTA
                }
                onLoginSuccessCallback={() =>
                  onSignInWithCodeSuccess('passcode recovery')
                }
                onSendCodeErrorCallback={errorMessage =>
                  trackAuthenticationPasscodeError(
                    'passcode recovery',
                    errorMessage,
                  )
                }
                onLoginErrorCallback={errorMessage =>
                  trackAuthenticationPasscodeError(
                    'passcode recovery',
                    errorMessage,
                  )
                }
                flags={flags}
              />
            )}
            {pageError && (
              <Alert variant="error" mb={6}>
                {pageError}
                {isLockedAccount && (
                  <>
                    <Text>
                      . {localiseText('auth_wizard.forgotten_message')}{' '}
                    </Text>
                    <TertiaryButton
                      onClick={onForgotPasswordClick}
                      data-testid="btnForgotPasswordModal"
                      inline
                    >
                      {localiseText('auth_wizard.forgotten_message_here')}
                    </TertiaryButton>
                    .
                  </>
                )}
              </Alert>
            )}
            {showUndeliverableEmailAlert && (
              <Alert variant="warning" mb={6}>
                <Heading level="h5" mb={3} color={'colorTextBody'}>
                  {localiseText('auth_wizard.validate_email_header')}
                </Heading>
                <Text>
                  {localiseText('auth_wizard.validate_email_message')}
                </Text>
              </Alert>
            )}
            {showSubmitButton && (
              <PrimaryButton
                type="submit"
                disabled={isLoading}
                width="100%"
                data-testid="authWizardSubmitButton"
              >
                {primaryCTAText()}
              </PrimaryButton>
            )}
            {showLoginWithCodeButton && (
              <>
                <SignInDivider />
                <SignInWithACodeButton onClick={onCodeLoginClick} />
              </>
            )}
            {showLoginWithPasswordButton && (
              <>
                <SignInDivider />
                <SignInWithPasswordButton onClick={onPasswordLoginClick} />
              </>
            )}
            {showBackButton && (
              <>
                <TertiaryButton
                  type="reset"
                  width="100%"
                  onClick={onBackClickToLogin}
                  data-testid="back-v1"
                >
                  {localiseText('auth_wizard.go_back')}
                </TertiaryButton>
              </>
            )}
          </Form>
          <ForgotPasswordModal
            isOpen={showForgotPasswordModal}
            onDismiss={() => setShowForgotPasswordModal(false)}
            email={getValues().email}
          />
          <ValidateEmailWarningModal
            isOpen={showValidateEmailModal}
            onDismiss={dismissEmailValidationModal}
            email={getValues().email}
            region={region}
          />
        </FormProvider>
        {showSocialLoginOptions && (
          <>
            <SocialLoginButtonsWrapper
              returnUrl={returnUrl}
              handleAccountRecovery={onForgotPasswordClick}
              setLoading={flag => setIsLoading(flag)}
            />
          </>
        )}
        {showTermsAndConditions && <TermsAndConditions region={region} />}
        {isLoading && (
          <LoadingOverlay label={localiseText('auth_wizard.common.loading')} />
        )}
      </Box>
    </>
  )
}
